// PermissionManagement.jsx
import React, { useState, useEffect } from 'react';
import '../members/MembersPage.css';
import { buildApiUrl, getAuthHeaders } from '../../config/api';
import { useRestaurant } from '../../context/RestaurantContext';
import { notify, confirm } from '../../context/FeedbackContext';
import { FaEdit, FaTrash, FaPlus, FaSave, FaTimes, FaToggleOn, FaToggleOff, FaChevronRight, FaChevronDown } from 'react-icons/fa';
import { getFilteredNavItemsWithIcons, getRouteFromItem } from '../../config/routes';
import HasPermi from '../../components/HasPermi'; // 导入权限组件

function PermissionManagement({ onNavigate }) {
  const { selectedRestaurant } = useRestaurant() || {};
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingPermission, setEditingPermission] = useState(null);
  const [expandedKeys, setExpandedKeys] = useState(new Set());
  const [formData, setFormData] = useState({
    permissionName: '',
    permissionCode: '',
    parentId: 0,
    type: 1,
    path: '',
    icon: '',
    sortOrder: 0,
    status: 1,
    description: ''
  });
  const [flatPermissions, setFlatPermissions] = useState([]);

  const permissionTypes = [
    { id: 1, name: 'Menu' },
    { id: 2, name: 'button' },
    { id: 3, name: 'interface' }
  ];

  const handleNavClick = (item) => {
    if (onNavigate) {
      if (item === 'Permission Management') {
        return;
      }
      onNavigate(getRouteFromItem(item));
    }
  };

  const flattenTree = (treeData, level = 0, result = []) => {
    treeData.forEach(item => {
      item.level = level;
      result.push(item);
      if (item.children && item.children.length) {
        flattenTree(item.children, level + 1, result);
      }
    });
    return result;
  };

  // 获取权限列表（树形结构）- 需要 permissions:tree 权限
  const fetchPermissions = async () => {
    setLoading(true);
    try {
      const res = await fetch(
        buildApiUrl(`/api/permission/tree`),
        { headers: getAuthHeaders() }
      );
      const json = await res.json();
      if (json.code === 200 && json.data) {
        setPermissions(json.data);
        const flat = flattenTree(json.data);
        setFlatPermissions(flat);
        const allExpanded = new Set();
        flat.forEach(p => allExpanded.add(p.id));
        setExpandedKeys(allExpanded);
      } else {
        console.error('Failed to fetch permissions:', json.msg || json.message);
        setPermissions([]);
        setFlatPermissions([]);
      }
    } catch (err) {
      console.error('Failed to fetch permissions', err);
      setPermissions([]);
      setFlatPermissions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPermissions();
  }, [selectedRestaurant]);

  const toggleExpand = (id) => {
    const newExpandedKeys = new Set(expandedKeys);
    if (newExpandedKeys.has(id)) {
      newExpandedKeys.delete(id);
    } else {
      newExpandedKeys.add(id);
    }
    setExpandedKeys(newExpandedKeys);
  };

  // 全部展开和全部收起不需要特定权限，只是UI操作
  const expandAll = () => {
    const allExpanded = new Set();
    flatPermissions.forEach(p => allExpanded.add(p.id));
    setExpandedKeys(allExpanded);
  };

  const collapseAll = () => {
    setExpandedKeys(new Set());
  };

  // 切换权限状态 - 需要 permissions:update 权限
  const handleToggleStatus = async (permission) => {
    const newStatus = permission.status === 1 ? 0 : 1;
    const action = newStatus === 1 ? 'enable' : 'disable';

    if (await confirm(`Are you sure you want to ${action} permission "${permission.permissionName}"?`)) {
      try {
        const updateData = {
          id: permission.id,
          permissionName: permission.permissionName,
          permissionCode: permission.permissionCode,
          parentId: permission.parentId || 0,
          type: permission.type,
          path: permission.path || '',
          icon: permission.icon || '',
          sortOrder: permission.sortOrder || 0,
          status: newStatus,
          description: permission.description || ''
        };

        const res = await fetch(
          buildApiUrl(`/api/permission/update`),
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...getAuthHeaders()
            },
            body: JSON.stringify(updateData)
          }
        );
        const json = await res.json();
        if (json.code === 200) {
          fetchPermissions();
          notify(newStatus === 1 ? 'Permission enabled successfully!' : 'Permission disabled successfully!', 'success');
        } else {
          notify(json.msg || json.message || `Failed to ${action} permission`, 'error');
        }
      } catch (err) {
        console.error('Failed to update permission status', err);
        notify(`Failed to ${action} permission`, 'error');
      }
    }
  };

  const handleOpenModal = (permission = null) => {
    if (permission) {
      setEditingPermission(permission);
      setFormData({
        permissionName: permission.permissionName,
        permissionCode: permission.permissionCode,
        parentId: permission.parentId || 0,
        type: permission.type,
        path: permission.path || '',
        icon: permission.icon || '',
        sortOrder: permission.sortOrder || 0,
        status: permission.status !== undefined ? permission.status : 1,
        description: permission.description || ''
      });
    } else {
      setEditingPermission(null);
      setFormData({
        permissionName: '',
        permissionCode: '',
        parentId: 0,
        type: 1,
        path: '',
        icon: '',
        sortOrder: 0,
        status: 1,
        description: ''
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingPermission(null);
    setFormData({
      permissionName: '',
      permissionCode: '',
      parentId: 0,
      type: 1,
      path: '',
      icon: '',
      sortOrder: 0,
      status: 1,
      description: ''
    });
  };

  const renderParentOptions = () => {
    return flatPermissions
      .filter(p => editingPermission ? p.id !== editingPermission.id : true)
      .map(p => {
        const prefix = '　'.repeat(p.level || 0) + ((p.level || 0) > 0 ? '├─ ' : '');
        return (
          <option key={p.id} value={p.id}>
            {prefix}{p.permissionName}
          </option>
        );
      });
  };

  // 提交表单 - 根据编辑或新增调用不同的接口
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.permissionName.trim()) {
      notify('Permission name is required', 'error');
      return;
    }
    if (!formData.permissionCode.trim()) {
      notify('Permission code is required', 'error');
      return;
    }

    try {
      // 根据是否有 editingPermission 决定使用 add 还是 update 接口
      const url = editingPermission
        ? buildApiUrl(`/api/permission/update`)
        : buildApiUrl(`/api/permission/add`);

      const payload = {
        permissionName: formData.permissionName,
        permissionCode: formData.permissionCode,
        parentId: formData.parentId || 0,
        type: formData.type,
        path: formData.path,
        icon: formData.icon,
        sortOrder: formData.sortOrder,
        status: formData.status,
        description: formData.description
      };

      if (editingPermission) {
        payload.id = editingPermission.id;
      }

      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(payload)
      });

      const json = await res.json();
      if (json.code === 200) {
        fetchPermissions();
        handleCloseModal();
        notify(editingPermission ? 'Permission updated successfully!' : 'Permission created successfully!', 'success');
      } else {
        notify(json.msg || json.message || 'Failed to save permission', 'error');
      }
    } catch (err) {
      console.error('Failed to save permission', err);
      notify('Failed to save permission', 'error');
    }
  };

  // 删除权限 - 需要 permissions:del 权限
  const handleDelete = async (permission) => {
    if (await confirm(`Are you sure you want to delete permission "${permission.permissionName}"?`)) {
      try {
        const res = await fetch(
          buildApiUrl(`/api/permission/del/${permission.id}`),
          {
            method: 'DELETE',
            headers: getAuthHeaders()
          }
        );
        const json = await res.json();
        if (json.code === 200) {
          fetchPermissions();
          notify('Permission deleted successfully!', 'success');
        } else {
          notify(json.msg || json.message || 'Failed to delete permission', 'error');
        }
      } catch (err) {
        console.error('Failed to delete permission', err);
        notify('Failed to delete permission', 'error');
      }
    }
  };

  const getStatusStyle = (status) => {
    return {
      whiteSpace: 'nowrap',
      display: 'inline-block',
      padding: '4px 12px',
      borderRadius: '20px',
      fontSize: '12px',
      fontWeight: 'bold',
      background: status === 1 ? '#d4edda' : '#f8d7da',
      color: status === 1 ? '#155724' : '#721c24'
    };
  };

  const getTypeName = (type) => {
    const found = permissionTypes.find(t => t.id === type);
    return found ? found.name : '-';
  };

  const renderTreeRows = (items, level = 0) => {
    let rows = [];
    for (const item of items) {
      const hasChildren = item.children && item.children.length > 0;
      const isExpanded = expandedKeys.has(item.id);
      const paddingLeft = 20 + level * 20;
      
      rows.push(
        <tr key={item.id} style={{ opacity: item.status === 0 ? 0.7 : 1 }}>
          <td style={{ textAlign: 'left', paddingLeft: `${paddingLeft}px` }}>
            {hasChildren && (
              <span 
                onClick={() => toggleExpand(item.id)}
                style={{ 
                  cursor: 'pointer', 
                  marginRight: '8px',
                  display: 'inline-block',
                  width: '16px',
                  color: '#666'
                }}
              >
                {isExpanded ? <FaChevronDown size={12} /> : <FaChevronRight size={12} />}
              </span>
            )}
            {!hasChildren && <span style={{ display: 'inline-block', width: '24px' }} />}
            <strong>{item.permissionName}</strong>
            {item.type === 2 && <span style={{ marginLeft: '8px', fontSize: '11px', color: '#888' }}>(button)</span>}
            {item.type === 3 && <span style={{ marginLeft: '8px', fontSize: '11px', color: '#888' }}>(interface)</span>}
          </td>
          
          <td style={{ textAlign: 'left' }}>
            <code style={{ background: '#f4f6fa', padding: '2px 6px', borderRadius: '4px', fontSize: '12px' }}>
              {item.permissionCode}
            </code>
          </td>
          
          <td style={{ textAlign: 'center' }}>
            <span style={{ 
              background: item.type === 1 ? '#e3f2fd' : item.type === 2 ? '#f3e5f5' : '#fff3e0',
              color: item.type === 1 ? '#1976d2' : item.type === 2 ? '#7b1fa2' : '#e65100',
              padding: '2px 8px',
              borderRadius: '12px',
              fontSize: '11px'
            }}>
              {getTypeName(item.type)}
            </span>
          </td>
          
          <td style={{ textAlign: 'center' }}>
            {item.path}
          </td>
          
          <td style={{ textAlign: 'center' }}>{item.sortOrder || 0}</td>
          
          <td style={{ textAlign: 'center' }}>
            <span style={getStatusStyle(item.status)}>
              {item.status === 1 ? 'Enable' : 'Disable'}
            </span>
          </td>
         
          <td style={{ textAlign: 'center' }}>
            {/* 编辑按钮 - 需要 permissions:update 权限 */}
            <HasPermi permission={['permissions:update']}>
              <button className="edit-btn" onClick={() => handleOpenModal(item)}>
                <FaEdit />
              </button>
            </HasPermi>
            
            {/* 状态切换按钮 - 需要 permissions:update 权限 */}
            <HasPermi permission={['permissions:update']}>
              <button 
                className="status-toggle-btn" 
                onClick={() => handleToggleStatus(item)}
                style={{ 
                  marginLeft: '8px', 
                  background: 'none', 
                  border: 'none', 
                  cursor: 'pointer', 
                  fontSize: '1.2rem',
                  color: item.status === 1 ? '#28a745' : '#6c757d',
                  verticalAlign: 'middle'
                }}
                title={item.status === 1 ? '点击停用' : '点击启用'}
              >
                {item.status === 1 ? <FaToggleOn /> : <FaToggleOff />}
              </button>
            </HasPermi>
            
            {/* 删除按钮 - 需要 permissions:del 权限 */}
            <HasPermi permission={['permissions:del']}>
              <button 
                className="delete-btn" 
                onClick={() => handleDelete(item)} 
                style={{ marginLeft: '8px', background: 'none', border: 'none', cursor: 'pointer', color: '#e74c3c' }}
              >
                <FaTrash />
              </button>
            </HasPermi>
          </td>
        </tr>
      );
      
      if (hasChildren && isExpanded) {
        rows = rows.concat(renderTreeRows(item.children, level + 1));
      }
    }
    return rows;
  };

  const totalCount = flatPermissions.length;
  const { items: navItems, icons: navIcons } = getFilteredNavItemsWithIcons();

  return (
    <div className="memberspage-container">
      <nav className="sidebar">
        <div className="sidebar-title">Restaurant Management</div>
        <ul>
          {navItems.map(item => (
            <li key={item} className={item === 'Permission Management' ? 'active' : ''}
              onClick={() => handleNavClick(item)}>
              <span className="nav-icon">{navIcons[item]}</span> {item}
            </li>
          ))}
        </ul>
      </nav>

      <main className="members-content">
        <div className="admin-breadcrumb">
          <span className="breadcrumb-link" onClick={() => handleNavClick('Home')}>Home</span>
          <span className="breadcrumb-sep">›</span>
          <span className="breadcrumb-current">Permission Management</span>
        </div>
        <h1 className="admin-page-title">Permission Management</h1>
        <p className="admin-page-subtitle">Manage system permissions. Control access to different modules and features.</p>

        <div className="user-list-card">
          <div className="user-list-header">
            <h2 className="user-list-title">Permission List</h2>
            <div className="user-list-actions">
              {/* 全部展开和全部收起不需要权限，只是UI操作 */}
              <button className="add-restaurant-btn" onClick={expandAll} style={{ marginRight: '8px' }}>
                Expand all
              </button>
              <button className="add-restaurant-btn" onClick={collapseAll} style={{ marginRight: '8px', background: '#6c757d' }}>
                Collapse all
              </button>
              
              {/* 创建权限按钮 - 需要 permissions:add 权限 */}
              <HasPermi permission={['permissions:add']}>
                <button className="add-restaurant-btn" onClick={() => handleOpenModal()}>
                  <FaPlus /> Create Permission
                </button>
              </HasPermi>
            </div>
          </div>          

          <table className="restaurant-table permission-management-table">
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>PERMISSION NAME</th>
                <th style={{ textAlign: 'left' }}>PERMISSION CODE</th>
                <th style={{ textAlign: 'center' }}>TYPE</th>
                <th style={{ textAlign: 'center' }}>PATH</th>
                <th style={{ textAlign: 'center' }}>SORT</th>
                <th style={{ textAlign: 'center' }}>STATUS</th>
                <th style={{ textAlign: 'center' }}>OPERATION</th>
              </tr>
            </thead>
            <tbody className="permission-table-body">
              {loading ? (
                <tr><td colSpan="7" style={{ textAlign: 'center', padding: '40px' }}>Loading...</td></tr>
              ) : permissions.length === 0 ? (
                <tr><td colSpan="7" style={{ textAlign: 'center', padding: '40px' }}>No permissions found. Create your first permission!</td></tr>
              ) : (
                renderTreeRows(permissions)
              )}
            </tbody>
          </table>
        </div>
      </main>

      {/* Modal for Create/Edit */}
      {showModal && (
        <div className="admin-modal-overlay" onClick={handleCloseModal}>
          <div className="admin-modal" onClick={e => e.stopPropagation()}>
            <div className="admin-modal-header" style={{ cursor: 'pointer' }} onClick={handleCloseModal}>
              <button className="admin-modal-close" onClick={handleCloseModal}>×</button>
              <h2 className="admin-modal-title">{editingPermission ? 'Edit Permission' : 'Create New Permission'}</h2>
              <p className="admin-modal-subtitle">{editingPermission ? 'Update permission information.' : 'Fill in the details to create a new permission.'}</p>
            </div>
            <div className="admin-modal-body" onClick={e => e.stopPropagation()}>
              <div className="form-field">
                <label>Parent Permission</label>
                <select
                  value={formData.parentId}
                  onChange={(e) => setFormData({ ...formData, parentId: parseInt(e.target.value) })}
                  style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ddd' }}
                >
                  <option value={0}>Top-level Menu</option>
                  {renderParentOptions()}
                </select>
              </div>
              <div className="form-field">
                <label>Permission Name <span className="required">*</span></label>
                <input
                  type="text"
                  value={formData.permissionName}
                  onChange={(e) => setFormData({ ...formData, permissionName: e.target.value })}
                  placeholder="e.g., User Management"
                />
              </div>
              <div className="form-field">
                <label>Permission Code <span className="required">*</span></label>
                <input
                  type="text"
                  value={formData.permissionCode}
                  onChange={(e) => setFormData({ ...formData, permissionCode: e.target.value })}
                  placeholder="e.g., user:manage"
                />
                <small style={{ color: '#888', fontSize: '11px' }}>Unique identifier used to determine permissions in code</small>
              </div>
              <div className="form-field">
                <label>Type</label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: parseInt(e.target.value) })}
                  style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ddd' }}
                >
                  {permissionTypes.map(type => (
                    <option key={type.id} value={type.id}>{type.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-field">
                <label>Path / URL</label>
                <input
                  type="text"
                  value={formData.path}
                  onChange={(e) => setFormData({ ...formData, path: e.target.value })}
                  placeholder="e.g., /users"
                />
              </div>
              {/* 当前无用 */}
              {/* <div className="form-field">
                <label>Icon</label>
                <input
                  type="text"
                  value={formData.icon}
                  onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                  placeholder="e.g., FaUser"
                />
              </div> */}
              <div className="form-field">
                <label>Sort Order</label>
                <input
                  type="number"
                  value={formData.sortOrder}
                  onChange={(e) => setFormData({ ...formData, sortOrder: parseInt(e.target.value) || 0 })}
                  placeholder="0"
                />
                <small style={{ color: '#888', fontSize: '11px' }}>The smaller the number, the earlier it appears</small>
              </div>
              <div className="form-field">
                <label>Status</label>
                <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="status"
                      value={1}
                      checked={formData.status === 1}
                      onChange={() => setFormData({ ...formData, status: 1 })}
                    />
                    <span style={getStatusStyle(1)}>enable</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="status"
                      value={0}
                      checked={formData.status === 0}
                      onChange={() => setFormData({ ...formData, status: 0 })}
                    />
                    <span style={getStatusStyle(0)}>disable</span>
                  </label>
                </div>
              </div>
              {/* <div className="form-field">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description of this permission"
                  rows="3"
                />
              </div> */}
            </div>
            <div className="admin-modal-footer" onClick={e => e.stopPropagation()}>
              <button className="cancel-btn" onClick={handleCloseModal}>Cancel</button>
              
              {/* 提交按钮 - 根据编辑或新增使用不同权限 */}
              <HasPermi permission={editingPermission ? ['permissions:update'] : ['permissions:add']}>
                <button className="submit-btn" onClick={handleSubmit}>
                  <FaSave /> {editingPermission ? 'Update Permission' : 'Create Permission'}
                </button>
              </HasPermi>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PermissionManagement;